/**
 * useEmailValidation Hook
 *
 * Custom React hook that provides debounced email validation with real-time
 * feedback for typo suggestions, disposable email warnings, and MX record checks.
 *
 * Features:
 * - Debounced validation (500ms default) to avoid excessive API calls
 * - Real-time validation status (validating, valid, invalid, suggestion)
 * - Comprehensive validation results (syntax, MX records, typos, disposable domains)
 * - Automatic cancellation of pending validations on unmount
 * - Memoized validation function to prevent unnecessary re-renders
 *
 * @example
 * const { validation, isValidating, validate, clearValidation } = useEmailValidation({
 *   checkMX: true,
 *   blockDisposable: true,
 *   debounceMs: 500,
 * });
 *
 * // In input onChange handler
 * const handleEmailChange = (e) => {
 *   const email = e.target.value;
 *   setEmail(email);
 *   validate(email);
 * };
 *
 * // Check validation state
 * if (validation?.isValid) {
 *   // Email is valid
 * } else if (validation?.suggestion) {
 *   // Show suggestion
 * }
 */

import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { emailValidationService } from "@shared/services";

/**
 * Debounce utility function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
function debounce(func, wait) {
  let timeout;

  const debounced = function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };

  debounced.cancel = () => {
    clearTimeout(timeout);
  };

  return debounced;
}

/**
 * Email validation hook with debounced validation
 *
 * @param {Object} options - Validation options
 * @param {boolean} [options.checkMX=true] - Perform DNS MX record lookup
 * @param {boolean} [options.checkTypos=true] - Suggest typo corrections
 * @param {boolean} [options.blockDisposable=true] - Block disposable emails
 * @param {boolean} [options.warnRole=true] - Warn about role accounts
 * @param {number} [options.timeout=3000] - DNS lookup timeout in ms
 * @param {number} [options.debounceMs=500] - Debounce delay in milliseconds
 * @returns {Object} Validation state and control functions
 */
export function useEmailValidation(options = {}) {
  const {
    checkMX = true,
    checkTypos = true,
    blockDisposable = true,
    warnRole = true,
    timeout = 3000,
    debounceMs = 500,
  } = options;

  const [validation, setValidation] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const abortControllerRef = useRef(null);

  /**
   * Validate email address with all configured checks
   * @param {string} email - Email address to validate
   */
  const validate = useCallback(
    async (email) => {
      // Clear validation if email is empty
      if (!email || typeof email !== "string" || email.trim() === "") {
        setValidation(null);
        setIsValidating(false);
        return;
      }

      // Cancel any pending validation
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this validation
      abortControllerRef.current = new AbortController();
      const currentAbortController = abortControllerRef.current;

      setIsValidating(true);

      try {
        const result = await emailValidationService.validateEmail(email, {
          checkMX,
          checkTypos,
          blockDisposable,
          warnRole,
          timeout,
        });

        // Only update state if this validation wasn't cancelled
        if (!currentAbortController.signal.aborted) {
          setValidation(result);
        }
      } catch (error) {
        // Only update error state if this validation wasn't cancelled
        if (!currentAbortController.signal.aborted) {
          console.error("Email validation error:", error);
          setValidation({
            isValid: false,
            email: email.toLowerCase().trim(),
            errors: ["Validation failed. Please check your email address."],
            warnings: [],
            confidence: "low",
            details: {
              syntaxValid: false,
              domainExists: false,
              hasMX: false,
              isDisposable: false,
              isRole: false,
              isFreeProvider: false,
            },
          });
        }
      } finally {
        // Only update loading state if this validation wasn't cancelled
        if (!currentAbortController.signal.aborted) {
          setIsValidating(false);
        }
      }
    },
    [checkMX, checkTypos, blockDisposable, warnRole, timeout],
  );

  /**
   * Debounced validation function
   * Memoized to prevent creating new debounced functions on every render
   */
  const debouncedValidate = useMemo(() => {
    const debounced = debounce(validate, debounceMs);

    return (email) => {
      // If email is empty, validate immediately (to clear state)
      if (!email || email.trim() === "") {
        validate(email);
      } else {
        // Otherwise use debounced version
        debounced(email);
      }
    };
  }, [validate, debounceMs]);

  /**
   * Clear validation state
   */
  const clearValidation = useCallback(() => {
    setValidation(null);
    setIsValidating(false);

    // Cancel any pending validation
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  /**
   * Cleanup on unmount - cancel any pending validations
   */
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    validation,
    isValidating,
    validate: debouncedValidate,
    clearValidation,
  };
}
